import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchQuotes } from '../../context/CommonContex';
import viewIcon from '../../statics/images/view.png';
import '../../App.css';

// Quote has "no Created On" when no line has created_on (same logic as Quotes.js getEarliestCreatedDate)
const getEarliestCreatedDate = (quote) => {
    if (!quote?.lines?.length) return '-';
    const dates = quote.lines
        .map(line => line.created_on)
        .filter(date => date)
        .sort();
    return dates.length > 0 ? dates[0].split(' ')[0] : '-';
};

const getEarliestCreatedDateForDays = (quote) => {
    if (quote?.quoted_on || quote?.quotedOn) return quote.quoted_on || quote.quotedOn;
    if (!quote?.lines?.length) return null;
    const dates = quote.lines
        .map(line => line.created_on)
        .filter(date => date)
        .sort();
    return dates.length > 0 ? dates[0] : null;
};

const getQuoteOwner = (quote) => {
    if (!quote?.lines?.length) return '-';
    const lineWithOwner = quote.lines.find(line => line.createdBy?.username);
    return lineWithOwner?.createdBy?.username || '-';
};

const isFirstQuote = (quote) => {
    if (!quote?.lines?.length) return 'No';
    return quote.lines.some(line => line.first_part_quote === 1) ? 'Yes' : 'No';
};

const getShippingComments = (quote) => {
    if (!quote?.lines?.length) return '-';
    const comments = quote.lines
        .map(line => line.order_comment2)
        .filter(comment => comment)
        .join(' | ');
    return comments || '-';
};

const getShippingDates = (quote) => {
    if (!quote?.lines?.length) return '-';
    const dates = quote.lines
        .map(line => line.promise_date)
        .filter(date => date)
        .join(', ');
    return dates || '-';
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(amount || 0);
};

const getQuoteTotal = (quote) => {
    if (!quote?.lines?.length) return '-';
    const total = quote.lines.reduce((sum, line) => {
        return sum + (Number(line.request_resale) || 0);
    }, 0);
    return total > 0 ? formatCurrency(total) : '-';
};

const calculateDaysInStage = (createdDate) => {
    if (!createdDate) return '-';
    try {
        const created = new Date(createdDate);
        if (Number.isNaN(created.getTime())) return '-';
        const today = new Date();
        created.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        const diffTime = today.getTime() - created.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const days = diffDays <= 0 ? 1 : diffDays;
        const match = String(createdDate).match(/(\d{1,2}):(\d{2})/);
        let time = '';
        if (match) {
            let hour = parseInt(match[1], 10);
            const minute = match[2];
            const ampm = hour >= 12 ? 'PM' : 'AM';
            hour = hour % 12 || 12;
            time = `${hour}:${minute} ${ampm}`;
        }
        return time ? `${days} Day / ${time}` : `${days} Day`;
    } catch (error) {
        return '-';
    }
};

const QuotesArchive = () => {
    const navigate = useNavigate();
    const [archivedQuotes, setArchivedQuotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const loadQuotes = async () => {
            try {
                setIsLoading(true);
                const response = await fetchQuotes(0, 10000, '');
                if (response && response.content) {
                    // Archive = quotes with no Created On (getEarliestCreatedDate returns '-')
                    const noCreatedOn = (response.content || []).filter(
                        quote => getEarliestCreatedDate(quote) === '-'
                    );
                    setArchivedQuotes(noCreatedOn);
                } else {
                    setArchivedQuotes([]);
                }
            } catch (error) {
                console.error('Error fetching quotes for archive:', error);
                if (error.response?.status === 401) {
                    localStorage.removeItem('jwt');
                    navigate('/login');
                }
                setArchivedQuotes([]);
            } finally {
                setIsLoading(false);
            }
        };
        loadQuotes();
    }, [navigate]);

    const filteredQuotes = searchTerm.trim()
        ? archivedQuotes.filter(quote => {
            const search = searchTerm.toLowerCase();
            const id = (quote.id || '').toString().toLowerCase();
            const customer = (quote.customer_name || '').toLowerCase();
            const status = (quote.status || '').toLowerCase();
            const owner = (getQuoteOwner(quote) || '').toLowerCase();
            return id.includes(search) || customer.includes(search) || status.includes(search) || owner.includes(search);
        })
        : archivedQuotes;

    return (
        <div className="box-body border b-white rounded-2">
            <div className="p-2 m-0 border-bottom row">
                <div className="col-12 d-flex justify-content-center align-items-center mb-3">
                    <div className="quotes-title">
                        <span className="quotes-text">Quotes Archive</span>
                    </div>
                </div>
                <p className="text-muted mb-0 px-2">
                    Quotes shown here have no &quot;Created On&quot; date (shown as &quot;-&quot; on the main Quotes page).
                </p>
            </div>

            <div className="row p-2">
                <div className="col-md-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Quote ID, Customer, Status, Owner..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="col-md-8 d-flex align-items-center">
                    <span className="text-muted">
                        Showing {filteredQuotes.length} quote{filteredQuotes.length !== 1 ? 's' : ''} with no Created On
                    </span>
                </div>
            </div>

            <div className="row m-0" style={{ width: '100%', margin: '0' }}>
                <div className="col-12 p-0 over-flow-table">
                    <table className="table table-hover mb-0 quotes-table">
                        <thead className="sticky-top">
                            <tr>
                                <th className="text-center" style={{ minWidth: '60px', maxWidth: '60px' }}>INFO</th>
                                <th className="text-center" style={{ minWidth: '120px', maxWidth: '120px' }}>QUOTE</th>
                                <th className="text-center" style={{ minWidth: '150px', maxWidth: '150px' }}>OWNER</th>
                                <th className="text-center" style={{ minWidth: '200px', maxWidth: '200px' }}>CUSTOMER NAME</th>
                                <th className="text-center" style={{ minWidth: '85px', maxWidth: '85px' }}>FIRST QUOTE</th>
                                <th className="text-center" style={{ minWidth: '95px', maxWidth: '95px' }}>STATUS</th>
                                <th className="text-center" style={{ minWidth: '110px', maxWidth: '110px' }}>CREATED ON</th>
                                <th className="text-center" style={{ minWidth: '100px', maxWidth: '100px' }}>TIME IN STAGE</th>
                                <th className="text-center" style={{ minWidth: '70px', maxWidth: '70px' }}>LINES #</th>
                                <th className="text-center" style={{ minWidth: '200px', maxWidth: '200px' }}>SHIPPING COMMENTS</th>
                                <th className="text-center" style={{ minWidth: '150px', maxWidth: '150px' }}>SHIPPING DATE</th>
                                <th className="text-center" style={{ minWidth: '150px', maxWidth: '150px' }}>QUOTE TOTAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan="12" className="text-center">
                                        <div className="d-flex justify-content-center align-items-center py-4">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <span className="ms-2">Loading quotes archive...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredQuotes.length > 0 ? (
                                filteredQuotes.map((quote) => {
                                    const earliestCreatedDate = getEarliestCreatedDateForDays(quote);
                                    return (
                                        <tr key={quote.id}>
                                            <td className="text-center">
                                                <a
                                                    href={`https://gigaflightinc.cetecerp.com/quote/${quote.id}/view`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title="View quote"
                                                >
                                                    <img src={viewIcon} alt="View" style={{ width: '20px' }} />
                                                </a>
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                    <a href={`https://gigaflightinc.cetecerp.com/quote/${quote.id}/view`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                                        {quote.id}
                                                    </a>
                                                    {quote.folder_url ? (
                                                        <a href={quote.folder_url} target="_blank" rel="noopener noreferrer" title="Open folder">
                                                            <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: '#ff8c00', cursor: 'pointer' }}></i>
                                                        </a>
                                                    ) : (
                                                        <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: '#cccccc' }} title="No folder URL"></i>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="text-center">{getQuoteOwner(quote)}</td>
                                            <td>
                                                {quote.customer_id ? (
                                                    <a
                                                        href={`https://gigaflightinc.cetecerp.com/customer/${quote.customer_id}/view`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ textDecoration: 'none' }}
                                                    >
                                                        {quote.customer_name || '-'}
                                                    </a>
                                                ) : (
                                                    quote.customer_name || '-'
                                                )}
                                            </td>
                                            <td className="text-center">{isFirstQuote(quote)}</td>
                                            <td className="text-center">
                                                <span className={`badge ${quote.status === 'Open' ? 'bg-success' : 'bg-secondary'}`}>
                                                    {quote.status || '-'}
                                                </span>
                                            </td>
                                            <td className="text-center">{getEarliestCreatedDate(quote)}</td>
                                            <td className="text-center">
                                                {earliestCreatedDate ? calculateDaysInStage(earliestCreatedDate) : '-'}
                                            </td>
                                            <td className="text-center">{quote?.lines?.length || 0}</td>
                                            <td className="shipping-comments-cell">
                                                <div className="shipping-comments-content">{getShippingComments(quote)}</div>
                                            </td>
                                            <td className="text-center">{getShippingDates(quote)}</td>
                                            <td className="text-center">{getQuoteTotal(quote)}</td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="12" className="text-center text-muted py-4">
                                        {archivedQuotes.length === 0 && !isLoading
                                            ? 'No quotes with missing Created On date found. All quotes have a Created On date.'
                                            : 'No matching quotes in archive.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default QuotesArchive;
